import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Save, ShieldCheck, Paperclip, Trash2, Plus, FlaskConical, AlertTriangle, Undo2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import {
  VALUE_STATUS, APPLICABILITY_LABELS, SOURCE_TYPE_LABELS, CATEGORY_LABELS, CATEGORY_STYLES,
} from "@/lib/strings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const NUMERIC_TYPES = ["INTEGER", "DECIMAL", "PERCENTAGE", "CURRENCY", "QUANTITY", "RATIO", "CALCULATED"];

function StatusBadge({ status }) {
  const meta = VALUE_STATUS[status] || VALUE_STATUS.MISSING;
  return (
    <Badge variant="outline" className={`${meta.className} text-[11px]`} data-testid={`value-status-${(status || "missing").toLowerCase()}`}>
      {meta.label}
    </Badge>
  );
}

function ApplicabilityBadge({ value, overridden }) {
  const styles = {
    APPLICABLE: "bg-green-50 text-green-800 border-green-300",
    NOT_APPLICABLE: "bg-slate-100 text-slate-500 border-slate-300",
    TO_BE_DETERMINED: "bg-amber-50 text-amber-800 border-amber-300",
  };
  return (
    <Badge variant="outline" className={`${styles[value]} text-[11px]`} data-testid="applicability-badge">
      {APPLICABILITY_LABELS[value]}{overridden ? " (override manuale)" : ""}
    </Badge>
  );
}

function ListEditor({ schema, records, onChange, disabled, testid }) {
  const items = Array.isArray(records) ? records : [];
  const update = (i, key, val) => {
    const next = items.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((rec, i) => (
        <div key={rec._key || i} className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50">
          <div className="flex justify-between items-center">
            <p className="text-xs font-semibold text-slate-600">Record {i + 1}</p>
            {!disabled && (
              <Button type="button" size="sm" variant="ghost" className="text-red-700 h-7"
                onClick={() => onChange(items.filter((_, idx) => idx !== i))} data-testid={`${testid}-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Rimuovi
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(schema || []).map((f) => (
              <div key={f.key} className={f.type === "LONG_TEXT" ? "md:col-span-2" : ""}>
                <Label className="text-xs">{f.label}</Label>
                {f.type === "ENUM" ? (
                  <Select value={rec[f.key] || ""} onValueChange={(v) => update(i, f.key, v)} disabled={disabled}>
                    <SelectTrigger className="h-8" data-testid={`${testid}-${i}-${f.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "LONG_TEXT" ? (
                  <Textarea rows={2} value={rec[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)}
                    disabled={disabled} data-testid={`${testid}-${i}-${f.key}`} />
                ) : (
                  <Input type={f.type === "INTEGER" || f.type === "DECIMAL" ? "number" : "text"} className="h-8"
                    value={rec[f.key] ?? ""} onChange={(e) => update(i, f.key, f.type === "INTEGER" || f.type === "DECIMAL" ? e.target.valueAsNumber : e.target.value)}
                    disabled={disabled} data-testid={`${testid}-${i}-${f.key}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {!disabled && (
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { _key: crypto.randomUUID() }])} data-testid={`${testid}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Aggiungi record
        </Button>
      )}
    </div>
  );
}

export default function VsmeDisclosure() {
  const { code } = useParams();
  const { companyId, period, can } = useTenant();
  const [detail, setDetail] = useState(null);
  const [edits, setEdits] = useState({});
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ applicability: "APPLICABLE", motivazione: "" });
  const [evidenceTarget, setEvidenceTarget] = useState(null);
  const [evidences, setEvidences] = useState([]);
  const [evidenceForm, setEvidenceForm] = useState({ name: "", description: "", reference: "" });

  const canWrite = can("esg:write");
  const canVerify = can("esg:verify");
  const canApprove = can("esg:approve");

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/vsme/disclosures/${code}`);
      setDetail(data);
      const initial = {};
      for (const dp of data.datapoints) {
        initial[dp.datapoint_code] = {
          numeric: dp.value?.numeric_value ?? null,
          text: dp.data_type === "LIST" && Array.isArray(dp.value?.text_value)
            ? dp.value.text_value.map((r) => ({ ...r, _key: crypto.randomUUID() }))
            : (dp.value?.text_value ?? null),
          boolean: dp.value?.boolean_value ?? null,
          source_type: dp.value?.source_type || "MANUAL",
          notes: dp.value?.notes || "",
          estimated: dp.value?.estimated || false,
        };
      }
      setEdits(initial);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api/toast/setState sono stabili
  }, [companyId, period?.id, code]);

  useEffect(() => { load(); }, [load]);

  const setEdit = (codeKey, patch) => setEdits((prev) => ({ ...prev, [codeKey]: { ...prev[codeKey], ...patch } }));

  const save = async (dp) => {
    const e = edits[dp.datapoint_code];
    const textValue = dp.data_type === "LIST" && Array.isArray(e.text)
      ? e.text.map(({ _key, ...rest }) => rest)
      : e.text;
    try {
      const { data } = await api.put(`/companies/${companyId}/periods/${period.id}/vsme/values`, {
        datapoint_code: dp.datapoint_code,
        numeric_value: e.numeric === "" ? null : e.numeric,
        text_value: textValue,
        boolean_value: e.boolean,
        source_type: e.source_type,
        notes: e.notes,
        estimated: e.estimated,
      });
      if (data.issues?.length) {
        toast.warning("Valore salvato con avvisi di validazione", {
          description: data.issues.map((i) => i.message).join(" "),
        });
      } else {
        toast.success("Valore salvato", { description: dp.label });
      }
      load();
    } catch (err) {
      toast.error("Errore salvataggio", { description: formatApiError(err) });
    }
  };

  const transition = async (dp, action) => {
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/vsme/values/${dp.value.id}/transition`, { action });
      toast.success("Stato aggiornato", { description: dp.label });
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const submitOverride = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/vsme/applicability`, {
        datapoint_code: overrideTarget.datapoint_code,
        applicability: overrideForm.applicability,
        motivazione: overrideForm.motivazione,
      });
      toast.success("Override di applicabilità registrato");
      setOverrideTarget(null);
      setOverrideForm({ applicability: "APPLICABLE", motivazione: "" });
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const removeOverride = async (dp) => {
    try {
      await api.delete(`/companies/${companyId}/periods/${period.id}/vsme/applicability/${dp.datapoint_code}`);
      toast.success("Override rimosso, applicabilità automatica ripristinata");
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const openEvidences = async (dp) => {
    setEvidenceTarget(dp);
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/vsme/values/${dp.value.id}/evidences`);
      setEvidences(data);
    } catch {
      setEvidences([]);
    }
  };

  const addEvidence = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/vsme/values/${evidenceTarget.value.id}/evidences`, evidenceForm);
      toast.success("Evidenza aggiunta");
      setEvidenceForm({ name: "", description: "", reference: "" });
      openEvidences(evidenceTarget);
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const deleteEvidence = async (ev) => {
    try {
      await api.delete(`/companies/${companyId}/periods/${period.id}/vsme/evidences/${ev.id}`);
      openEvidences(evidenceTarget);
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  if (!detail) {
    return <p className="text-sm text-slate-600" data-testid="disclosure-loading">Caricamento in corso…</p>;
  }

  const d = detail.disclosure;
  const notApplicable = (dp) => dp.applicability === "NOT_APPLICABLE";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="vsme-disclosure-page">
      <div>
        <Link to="/vsme" className="inline-flex items-center gap-1.5 text-sm text-green-700 font-semibold hover:underline mb-3" data-testid="back-to-vsme">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Torna a VSME
        </Link>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
          {d.disclosure_code} — {d.title}
        </h1>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Badge variant="outline" className={CATEGORY_STYLES[d.category]}>{CATEGORY_LABELS[d.category]}</Badge>
          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300">
            {d.module === "BASIC" ? "Basic Module" : "Comprehensive Module"}
          </Badge>
          <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300" data-testid="disclosure-completion-badge">
            {detail.stats.completed}/{detail.stats.applicable} compilati ({detail.stats.completion}%)
          </Badge>
        </div>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-3xl">{d.description}</p>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-3" data-testid="technical-mode-banner">
        <FlaskConical className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-sky-900 leading-relaxed">
          Il motore VSME organizza i dati inseriti nel questionario e mostra lo stato
          delle informazioni richieste dallo standard.
          Standard: {detail.standard_version.version_name}.
        </p>
      </div>

      <div className="space-y-4">
        {detail.datapoints.map((dp) => {
          const e = edits[dp.datapoint_code] || {};
          const status = dp.present ? (dp.value?.status || "DRAFT") : "MISSING";
          const disabled = !canWrite || notApplicable(dp);
          return (
            <Card key={dp.id} className={`border-slate-200 ${notApplicable(dp) ? "opacity-70 bg-slate-50" : ""}`}
              data-testid={`datapoint-${dp.datapoint_code}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="max-w-2xl">
                    <div className="font-semibold text-slate-900 text-sm flex flex-wrap items-center gap-2">
                      {dp.label}
                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600">
                        {dp.required_type === "REQUIRED" ? "Obbligatorio" : dp.required_type === "CONDITIONAL" ? "Condizionato" : "Facoltativo"}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 font-mono mt-0.5">{dp.datapoint_code}</p>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">{dp.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <StatusBadge status={status} />
                    <ApplicabilityBadge value={dp.applicability} overridden={dp.applicability_overridden} />
                  </div>
                </div>

                {dp.issues?.length > 0 && (
                  <div className="space-y-1" data-testid={`issues-${dp.datapoint_code}`}>
                    {dp.issues.map((i) => (
                      <p key={i.rule_code} role="alert" className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" /> {i.message}
                      </p>
                    ))}
                  </div>
                )}

                {!notApplicable(dp) && (
                  <div className="space-y-3">
                    {dp.data_type === "BOOLEAN" && (
                      <Select
                        value={e.boolean === null || e.boolean === undefined ? "" : String(e.boolean)}
                        onValueChange={(v) => setEdit(dp.datapoint_code, { boolean: v === "" ? null : v === "true" })}
                        disabled={disabled}>
                        <SelectTrigger className="w-[220px]" data-testid={`input-${dp.datapoint_code}`}>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Sì</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {dp.data_type === "ENUM" && (
                      <Select value={e.text || ""} onValueChange={(v) => setEdit(dp.datapoint_code, { text: v || null })} disabled={disabled}>
                        <SelectTrigger className="w-[260px]" data-testid={`input-${dp.datapoint_code}`}>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          {(dp.enum_options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {NUMERIC_TYPES.includes(dp.data_type) && (
                      <div className="flex items-center gap-2">
                        <Input type="number" step="any" className="w-[220px]" value={e.numeric ?? ""}
                          onChange={(ev) => setEdit(dp.datapoint_code, { numeric: ev.target.value === "" ? null : ev.target.valueAsNumber })}
                          disabled={disabled} data-testid={`input-${dp.datapoint_code}`} />
                        {dp.unit && <span className="text-sm text-slate-600 font-medium">{dp.unit}</span>}
                      </div>
                    )}
                    {(dp.data_type === "TEXT" || dp.data_type === "REFERENCE") && (
                      <Input value={e.text || ""} onChange={(ev) => setEdit(dp.datapoint_code, { text: ev.target.value || null })}
                        disabled={disabled} data-testid={`input-${dp.datapoint_code}`} />
                    )}
                    {dp.data_type === "DATE" && (
                      <Input type="date" className="w-[220px]" value={e.text || ""}
                        onChange={(ev) => setEdit(dp.datapoint_code, { text: ev.target.value || null })}
                        disabled={disabled} data-testid={`input-${dp.datapoint_code}`} />
                    )}
                    {dp.data_type === "LONG_TEXT" && (
                      <Textarea rows={3} value={e.text || ""} onChange={(ev) => setEdit(dp.datapoint_code, { text: ev.target.value || null })}
                        disabled={disabled} data-testid={`input-${dp.datapoint_code}`} />
                    )}
                    {dp.data_type === "LIST" && (
                      <ListEditor schema={dp.item_schema} records={e.text || []}
                        onChange={(records) => setEdit(dp.datapoint_code, { text: records.length ? records : null })}
                        disabled={disabled} testid={`list-${dp.datapoint_code}`} />
                    )}

                    <div className="flex flex-wrap items-end gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Fonte del dato</Label>
                        <Select value={e.source_type} onValueChange={(v) => setEdit(dp.datapoint_code, { source_type: v })} disabled={disabled}>
                          <SelectTrigger className="w-[200px] h-8" data-testid={`source-${dp.datapoint_code}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(SOURCE_TYPE_LABELS).map(([k, l]) => (
                              <SelectItem key={k} value={k} data-testid={`source-option-${k}`}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1 flex-1 min-w-[200px]">
                        <Label className="text-xs">Note</Label>
                        <Input className="h-8" value={e.notes} onChange={(ev) => setEdit(dp.datapoint_code, { notes: ev.target.value })}
                          disabled={disabled} data-testid={`notes-${dp.datapoint_code}`} />
                      </div>
                      {canWrite && (
                        <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => save(dp)} data-testid={`save-${dp.datapoint_code}`}>
                          <Save className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Salva
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
                  {dp.value && canWrite && dp.value.status === "DRAFT" && (
                    <Button size="sm" variant="outline" onClick={() => transition(dp, "to_verify")} data-testid={`toverify-${dp.datapoint_code}`}>
                      Segna da verificare
                    </Button>
                  )}
                  {dp.value && canVerify && ["DRAFT", "TO_VERIFY", "ESTIMATED", "IMPORTED"].includes(dp.value.status) && (
                    <Button size="sm" variant="outline" onClick={() => transition(dp, "verify")} data-testid={`verify-${dp.datapoint_code}`}>
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Verifica
                    </Button>
                  )}
                  {dp.value && canApprove && dp.value.status === "VERIFIED" && (
                    <Button size="sm" variant="outline" className="text-green-800 border-green-300" onClick={() => transition(dp, "approve")} data-testid={`approve-${dp.datapoint_code}`}>
                      Approva
                    </Button>
                  )}
                  {dp.value && canVerify && ["VERIFIED", "APPROVED"].includes(dp.value.status) && (
                    <Button size="sm" variant="ghost" onClick={() => transition(dp, "back_to_draft")} data-testid={`reopen-${dp.datapoint_code}`}>
                      <Undo2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Riporta a bozza
                    </Button>
                  )}
                  {canVerify && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => { setOverrideTarget(dp); }} data-testid={`override-${dp.datapoint_code}`}>
                        Override applicabilità
                      </Button>
                      {dp.applicability_overridden && (
                        <Button size="sm" variant="ghost" className="text-red-700" onClick={() => removeOverride(dp)} data-testid={`remove-override-${dp.datapoint_code}`}>
                          Rimuovi override
                        </Button>
                      )}
                    </>
                  )}
                  {dp.value && (
                    <Button size="sm" variant="ghost" onClick={() => openEvidences(dp)} data-testid={`evidences-${dp.datapoint_code}`}>
                      <Paperclip className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Evidenze ({dp.evidence_count})
                    </Button>
                  )}
                  {dp.value?.updated_at && (
                    <span className="text-[11px] text-slate-500 ml-auto">
                      Ultima modifica: {new Date(dp.value.updated_at).toLocaleString("it-IT")}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!overrideTarget} onOpenChange={() => setOverrideTarget(null)}>
        <DialogContent data-testid="override-dialog">
          <DialogHeader><DialogTitle>Override manuale di applicabilità</DialogTitle></DialogHeader>
          {overrideTarget && (
            <form onSubmit={submitOverride} className="space-y-4">
              <p className="text-sm text-slate-700">
                Datapoint: <strong>{overrideTarget.datapoint_code}</strong> — stato automatico attuale:{" "}
                <strong>{APPLICABILITY_LABELS[overrideTarget.applicability]}</strong>
              </p>
              <div className="space-y-1.5">
                <Label>Nuovo stato</Label>
                <Select value={overrideForm.applicability} onValueChange={(v) => setOverrideForm({ ...overrideForm, applicability: v })}>
                  <SelectTrigger data-testid="override-applicability-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="APPLICABLE">Applicabile</SelectItem>
                    <SelectItem value="NOT_APPLICABLE">Non applicabile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="override-motivation">Motivazione * (registrata nell'Audit Log)</Label>
                <Textarea id="override-motivation" required minLength={5} rows={3}
                  value={overrideForm.motivazione}
                  onChange={(e) => setOverrideForm({ ...overrideForm, motivazione: e.target.value })}
                  data-testid="override-motivation-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOverrideTarget(null)} data-testid="override-cancel">Annulla</Button>
                <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="override-submit">Registra override</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!evidenceTarget} onOpenChange={() => setEvidenceTarget(null)}>
        <DialogContent className="max-w-xl" data-testid="evidence-dialog">
          <DialogHeader><DialogTitle>Evidenze — {evidenceTarget?.datapoint_code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {evidences.length === 0 && (
              <p className="text-sm text-slate-600" data-testid="evidences-empty">Nessuna evidenza collegata.</p>
            )}
            {evidences.map((ev) => (
              <div key={ev.id} className="flex items-start justify-between gap-3 border border-slate-200 rounded-md p-3" data-testid={`evidence-${ev.id.slice(0, 8)}`}>
                <div>
                  <p className="text-sm font-medium text-slate-900">{ev.name}</p>
                  {ev.description && <p className="text-xs text-slate-600">{ev.description}</p>}
                  {ev.reference && <p className="text-xs text-slate-500 font-mono mt-1">{ev.reference}</p>}
                </div>
                {canWrite && (
                  <Button size="sm" variant="ghost" className="text-red-700" onClick={() => deleteEvidence(ev)} data-testid={`delete-evidence-${ev.id.slice(0, 8)}`}>
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            ))}
            {canWrite && (
              <form onSubmit={addEvidence} className="border-t border-slate-200 pt-3 space-y-3">
                <p className="text-xs font-semibold text-slate-700">Aggiungi evidenza (riferimento documentale)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nome *</Label>
                    <Input required value={evidenceForm.name}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, name: e.target.value })} data-testid="evidence-name-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Riferimento (es. nome file, protocollo)</Label>
                    <Input value={evidenceForm.reference}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, reference: e.target.value })} data-testid="evidence-reference-input" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Descrizione</Label>
                  <Input value={evidenceForm.description}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, description: e.target.value })} data-testid="evidence-description-input" />
                </div>
                <Button type="submit" size="sm" className="bg-green-700 hover:bg-green-800" data-testid="evidence-submit">
                  <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Aggiungi evidenza
                </Button>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
